import React from 'react'
import { FormattedMessage } from 'react-intl'
import capitalizeString from 'utils/capitalize'
import ArrowRight24 from 'assets/icons/arrow-right_24px'
import { SitesTableIconConfirmed, SitesTableIconDraft, ViewActionStyle } from './style'
import { formatDateYear } from 'utils/formatDateYear'
import {
  SITE_STATUS_CONFIRMED_ID,
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_EDIT_ONLY_ID,
  SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
} from 'constants/siteStatuses'
import { resolveEditOnlyRouteIds } from 'utils/editOnlyNavigation'
import ActionMenu from 'features/ActionMenu'
import { createNavigateReviewDetails } from 'utils/navigationHandlers'

function isEditOnlyStatus (statusId) {
  return statusId === SITE_STATUS_EDIT_ONLY_ID || statusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
}

function normalizeReassessmentSite (site, parentName) {
  const statusId = site.SiteStatusId
  const editOnlySiteStatus = isEditOnlyStatus(statusId)
  return {
    ...site,
    name: site.name || parentName,
    ProposerId: site.ProposerId || site.proposerId,
    SiteStatus: site.SiteStatus || { id: statusId },
    reassessmentFlag: !editOnlySiteStatus,
    historyId: editOnlySiteStatus ? (site.sitehistoryId || site.historyId) : site.historyId
  }
}

function buildViewAction () {
  return (
    <ViewActionStyle>
      <p>View</p>
      <ArrowRight24 />
    </ViewActionStyle>
  )
}

/** Row ids for Table rowOnClick: parent site id + edit-only pk (or history id). */
export function getReassessmentRowNavigationIds (site) {
  const editOnlySiteStatus = isEditOnlyStatus(site.SiteStatusId)
  if (editOnlySiteStatus) {
    const { siteId, editOnlySiteId } = resolveEditOnlyRouteIds(site)
    return {
      id: siteId,
      historyId: editOnlySiteId,
      siteStatusId: site.SiteStatusId
    }
  }
  return {
    id: site.id,
    historyId: site.historyId,
    siteStatusId: site.SiteStatusId
  }
}

export const canManageReassessmentDraftActions = (site, profile) => {
  if (!site || !profile) return false
  return site.SiteStatusId === SITE_STATUS_DRAFT_ID && site.ProposerId === profile.id
}

export const makeSitesRows = (sites = [], optionsOrActiveTab) => {
  const isOptions = typeof optionsOrActiveTab === 'object' && optionsOrActiveTab !== null
  const options = isOptions ? optionsOrActiveTab : { activeTab: optionsOrActiveTab }
  const {
    activeTab,
    parentSiteName,
    profile,
    navigate,
    setSiteForDeleting
  } = options

  if (activeTab === 'biodiversity') {
    return sites
      .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt))
      .map((site) => {
        const formattedDate = formatDateYear(site?.updatedAt)
        const rawTime = site.createdAt ? new Date(site.createdAt).toLocaleTimeString([], { hour12: true }) : new Date().toLocaleTimeString([], { hour12: true })
        const time = rawTime === '12:00:00 AM' || rawTime === '12:00:00 am' ? '-' : rawTime
        return {
          name: `${capitalizeString(site.Users.firstName)} ${capitalizeString(site.Users.lastName)}`,
          date: formattedDate,
          time,
          status:
            site.monitoring_status === SITE_STATUS_CONFIRMED_ID ? (
              <>
                <SitesTableIconConfirmed />
                Confirmed
              </>
            ) : (
              <>
                <SitesTableIconDraft />
                Draft
              </>
            ),
          actions: buildViewAction(),
          id: site.siteId,
          monitoringId: site.id
        }
      })
  }

  return [...sites]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .map((site) => {
      const formattedDate = formatDateYear(site.createdAt)
      const time = site.createdAt
        ? new Date(site.createdAt).toLocaleTimeString([], { hour12: true })
        : new Date().toLocaleTimeString([], { hour12: true })
      const normalizedSite = normalizeReassessmentSite(site, parentSiteName)
      const navIds = getReassessmentRowNavigationIds(normalizedSite)
      const showDraftActions = canManageReassessmentDraftActions(normalizedSite, profile)

      const actions = showDraftActions ? (
        <ActionMenu
          dropdownItems={[
            {
              title: 'edit',
              onClick: createNavigateReviewDetails(normalizedSite, navigate)
            },
            {
              title: 'Remove',
              onClick: () => setSiteForDeleting({
                id: site.id,
                historyId: site.historyId,
                reassessmentFlag: true,
                name: parentSiteName
              })
            }
          ]}
        />
      ) : (
        buildViewAction()
      )

      return {
        proposer: `${capitalizeString(site.User?.lastName)} ${capitalizeString(
          site.User?.firstName
        )}`,
        date: formattedDate,
        time,
        actions,
        id: navIds.id,
        historyId: navIds.historyId,
        siteStatusId: navIds.siteStatusId
      }
    })
}

export const makeSitesColumns = (activeTab) => {
  if (activeTab === 'biodiversity') {
    return [
      {
        id: 'name',
        header: (
          <>
            <FormattedMessage id="categoryName" />
          </>
        ),
        accessorKey: 'name',
        style: {
          width: '20%'
        },
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'date',
        header: (
          <>
            <FormattedMessage id="date" />
          </>
        ),
        accessorKey: 'date',
        style: {
          width: '20%'
        },
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'time',
        header: (
          <>
            <FormattedMessage id="time" />
          </>
        ),
        accessorKey: 'time',
        style: {
          width: '17%'
        },
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'status',
        header: (
          <>
            <FormattedMessage id="status" />
          </>
        ),
        accessorKey: 'status',
        style: {
          width: '17%'
        },
        cell: ({ getValue }) => getValue()
      },
      {
        id: 'actions',
        header: <FormattedMessage id="actions" />,
        accessorKey: 'actions',
        style: {
          width: '14%'
        },
        cell: ({ getValue }) => getValue()
      }
    ]
  }

  return [
    {
      id: 'proposer',
      header: (
        <>
          <FormattedMessage id="proposer" />
        </>
      ),
      accessorKey: 'proposer',
      style: {
        width: '27%'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'date',
      header: (
        <>
          <FormattedMessage id="date" />
        </>
      ),
      accessorKey: 'date',
      style: {
        width: '23%'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'time',
      header: (
        <>
          <FormattedMessage id="time" />
        </>
      ),
      accessorKey: 'time',
      style: {
        width: '13%'
      },
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'actions',
      header: <FormattedMessage id="actions" />,
      accessorKey: 'actions',
      style: {
        width: '15%'
      },
      cell: ({ getValue }) => getValue()
    }
  ]
}
